import "@/App.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-8 py-4 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_24px_rgba(212,175,116,0.35)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Package = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/packages/${slug}`)
      .then((r) => { if (alive) { setData(r.data); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [slug]);

  if (!loaded) {
    return (
      <div className="min-h-screen bg-[#0a0604] text-[#a89878] flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0604] text-[#f4d089] flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-3xl">Package not found</h1>
        <a href="/" className="text-[#f4d089] underline">Go back home</a>
      </div>
    );
  }

  const goBook = () => {
    window.location.href = `/book?package=${encodeURIComponent(data.name)}`;
  };
  const price = data.base_price ? Number(data.base_price).toLocaleString("en-IN") : "—";

  return (
    <div className="min-h-screen bg-[#0a0604] text-[#e8dcc0]" data-testid={`package-page-${slug}`}>
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <a href="/" className="flex items-center" data-testid="cinevibe-logo">
  <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
    <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
  </div>
</a>
          <GoldButton onClick={goBook} className="text-sm px-5 py-2.5">BOOK NOW</GoldButton>
        </Section>
      </header>

      <div className="relative h-[420px] sm:h-[520px] w-full overflow-hidden">
        {data.image && <img src={data.image} alt={data.name} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-[#0a0604]/55 to-[#0a0604]/30" />
        <Section className="absolute inset-x-0 bottom-0 pb-10">
          {data.badge && (
            <span className="inline-block bg-[#f4d089] text-[#1a0f06] text-[11px] font-bold tracking-wider px-3 py-1 rounded mb-4">
              {data.badge}
            </span>
          )}
          <h1 className="font-display text-white text-3xl sm:text-5xl leading-tight max-w-3xl">{data.name}</h1>
          <p className="text-[#d8cdb3] mt-3 max-w-2xl">{data.sub}</p>
          <div className="text-[#f4d089] mt-4 text-sm">Starting at <b>₹{price}</b></div>
        </Section>
      </div>

      <Section className="py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white">{data.name}</h2>
          <p className="text-[#bfb393] mt-4 leading-relaxed text-[15px] whitespace-pre-line">{data.description}</p>
        </div>
        <div className="flex justify-center mt-12">
          <GoldButton onClick={goBook} className="text-base tracking-[0.15em]">
            BOOK THIS PACKAGE NOW
          </GoldButton>
        </div>
      </Section>

      <div className="border-t border-[#3a2a14]/60 bg-gradient-to-b from-[#0d0905] to-[#14100a]">
        <Section className="py-16 text-center">
          <h3 className="font-display text-3xl sm:text-4xl text-white">Ready to make it unforgettable?</h3>
          <p className="text-[#bfb393] mt-3 max-w-xl mx-auto">
            Instant confirmation. Premium decor. Private theatre. Starting at <span className="text-[#f4d089] font-semibold">₹{price}</span>.
          </p>
          <div className="flex justify-center mt-8">
            <GoldButton onClick={goBook} className="text-base tracking-[0.18em] flex items-center gap-2">
              BOOK A PRIVATE SCREEN NOW <ArrowRight size={18} />
            </GoldButton>
          </div>
        </Section>
      </div>

      <footer className="border-t border-[#3a2a14]/60 py-6 text-center text-xs text-[#a89878] leading-relaxed">
        © {new Date().getFullYear()} The CineVibe Private Screens & Café<br />
        BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
      </footer>
    </div>
  );
};

export default Package;
